import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "axios";
import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
  type HubConnection,
  JsonHubProtocol,
} from "@microsoft/signalr";
import CHAT_API from "@/api/classes/chat";
import { createNotification } from "@/tools/notification";
import {
  type GetMessageDto,
  type AddMessageDto,
  type AdminChatFilterDto,
  type UserChatFilterDto,
  type SupportUserMessage,
  type SupportAdmin,
  type ChatBloDto,
  RequestStatus,
  type ChatIdsFilterDto,
} from "@/api/api.dto";
import { API_URL } from "@/api";
import { useUser } from "@/stores/user/user";
import router from "@/router";
import { useSupportAdminStore } from "@/stores/support/support-admin.store";
import { useSupportUserStore } from "@/stores/support/support-user.store";

export const useChatStore = defineStore("chatStore", () => {
  const userStore = useUser();
  const supportAdminStore = useSupportAdminStore();
  const supportUserStore = useSupportUserStore();

  const messages = ref<Record<number, GetMessageDto[]>>({});
  const totalMessagesCount = ref<Record<number, number>>({});
  const pageSize = ref<number>(100);
  const currentPageNumbers = ref<Record<number, number>>({});
  const unreadMessagesCount = ref<Record<number, number>>({});
  const unreadGroupsMessagesCount = ref<Partial<Record<RequestStatus, number>>>(
    {},
  );
  const connectedRequestIds = ref<number[]>([]);

  const isLoadingChats = ref<boolean>(false);
  const isLoadingMessages = ref<boolean>(false);
  const isSendingMessage = ref<boolean>(false);

  const supportAdmins = ref<SupportAdmin[]>([]);
  const isLoadingAdmins = ref<boolean>(false);

  const currentChatId = computed<number | null>(() => {
    const chatId = router.currentRoute.value.params.supportRequestId as string;
    return chatId ? parseInt(chatId, 10) : null;
  });

  const currentRequest = ref<ChatBloDto | null>(null);

  const currentSelectedStatus = computed(() => {
    if (!currentRequest.value) return null;
    return getRequestStatus(currentRequest.value);
  });

  const hasCurrentChatPreviousPage = computed(() => {
    if (!currentChatId.value) return false;
    if (
      !totalMessagesCount.value ||
      !totalMessagesCount.value[currentChatId.value]
    )
      return false;
    const currentPageNumber =
      currentPageNumbers.value[currentChatId.value] || 0;
    return (
      (totalMessagesCount.value[currentChatId.value] as number) >
      (currentPageNumber + 1) * pageSize.value
    );
  });

  const getRequestStatus = (request: ChatBloDto): RequestStatus => {
    if (request.isClosed) return RequestStatus.CLOSED;
    if (userStore.isUserSupport) {
      if (request.isSolved) return RequestStatus.ANSWERED;
      if (request.adminId) return RequestStatus.IN_WORK;
    }
    return userStore.isUserSupport ? RequestStatus.NEW : RequestStatus.IN_WORK;
  };

  // WebSocket (SignalR)
  const connection = ref<HubConnection | null>(null);
  const connectionState = ref<HubConnectionState>(
    HubConnectionState.Disconnected,
  );

  const isConnected = computed(
    () => connectionState.value === HubConnectionState.Connected,
  );

  const currentMessages = computed<SupportUserMessage[]>(() => {
    if (!currentChatId.value) return [];
    return (messages.value[currentChatId.value] || []).map((msg) => ({
      author: msg.authorName || "Имя неизвестно",
      authorId: msg.messageAuthorId || null,
      self: msg.messageAuthorId === userStore.user?.id,
      text: msg.message || "",
      createdDate: msg.createdDate,
      type: msg.type,
      imagePath: msg.imagePath,
    }));
  });

  // Error handler
  const onError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
      console.error("Chat API Error:", err);
      createNotification(
        "error",
        err.response?.data?.detail ||
          err.response?.data ||
          "Ошибка при работе с чатом",
        undefined,
        "topRight",
      );
    } else {
      console.error("Unexpected error:", err);
      createNotification(
        "error",
        "Произошла непредвиденная ошибка",
        undefined,
        "topRight",
      );
    }
  };

  // WebSocket methods
  const connectWebSocket = async () => {
    if (connection.value || isConnected.value) return;

    const hubUrl = `${API_URL}/chathub`;

    try {
      console.log("Connecting to SignalR Hub:", hubUrl);

      connection.value = new HubConnectionBuilder()
        .withUrl(`${hubUrl}?access_token=${userStore.token}`, {
          transport: HttpTransportType.WebSockets,
          withCredentials: true,
        })
        .withHubProtocol(new JsonHubProtocol())
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Error)
        .build();

      connection.value.on("ReceiveMessage", async (message: GetMessageDto) => {
        console.log("message received: ", message);
        if (!messages.value[message.chatId]) {
          messages.value[message.chatId] = [];
        }
        messages.value[message.chatId]!.push(message);

        if (currentChatId.value !== message.chatId) {
          if (!unreadMessagesCount.value[message.chatId]) {
            unreadMessagesCount.value[message.chatId] = 0;
          }
          unreadMessagesCount.value[message.chatId]!++;

          try {
            const messageChat = await CHAT_API.getRequestById(message.chatId);
            const chatStatus = getRequestStatus(messageChat.data);
            if (!unreadGroupsMessagesCount.value[chatStatus]) {
              unreadGroupsMessagesCount.value[chatStatus] = 0;
            }
            unreadGroupsMessagesCount.value[chatStatus]!++;
          } catch (err) {
            console.error("Error getting unread group messages count:", err);
          }
        }

        await fetchChats();
        if (!connectedRequestIds.value.includes(message.chatId)) {
          await connectToAllUsersChats();
        }
      });

      connection.value.onreconnecting(() => {
        connectionState.value = HubConnectionState.Reconnecting;
      });

      connection.value.onreconnected(() => {
        connectToAllUsersChats()
          .then(() => console.log("All users chats connected"))
          .catch((err) =>
            console.error("Error connecting to all users chats:", err),
          );
        connectionState.value = HubConnectionState.Connected;
      });

      connection.value.onclose(() => {
        connectionState.value = HubConnectionState.Disconnected;
        connection.value = null;
      });

      await connection.value.start();
      connectionState.value = HubConnectionState.Connected;
      console.log("SignalR connected to:", hubUrl);

      await connectToAllUsersChats();
    } catch (err: any) {
      console.error("Error connecting SignalR:", err);
      console.dir(err); // Provide more detailed error object inspection
      if (err.toString().includes("404")) {
        console.warn(
          "SignalR Hub not found at",
          hubUrl,
          ". Please verify the Hub URL on the backend and ensure WebSockets are enabled.",
        );
      } else if (
        err.toString().includes("unauthorized") ||
        err.toString().includes("401")
      ) {
        console.warn(
          "SignalR connection unauthorized. Ensure HttpOnly cookies are correctly set and backend allows them for WebSocket upgrades.",
        );
      }
      connectionState.value = HubConnectionState.Disconnected;
      connection.value = null;
    }
  };

  const connectToChat = async (chatId: number) => {
    await invokeRemoteSignalMethod("JoinChat", chatId);
    console.log("SignalR connected to chat:", chatId);
  };

  const connectToChats = async (chatIds: number[]) => {
    await invokeRemoteSignalMethod("JoinChats", chatIds);
    console.log("SignalR connected to chats:", chatIds);
  };

  const addMessageToChatWS = async (messageData: AddMessageDto) => {
    await invokeRemoteSignalMethod("AddMessage", messageData);
    console.log("SignalR message added to chat:", messageData.chatId);
  };

  const solveChatWS = async (chatId: number) => {
    await invokeRemoteSignalMethod("SolveChat", chatId);
    console.log("SignalR chat solved:", chatId);
    createNotification(
      "success",
      "Обращение отмечено как решенное",
      undefined,
      "topRight",
    );
  };

  const closeChatWS = async (chatId: number) => {
    await invokeRemoteSignalMethod("CloseChat", chatId);
    console.log("SignalR chat closed:", chatId);
    createNotification("success", "Обращение закрыто", undefined, "topRight");
  };

  const disconnectWebSocket = async () => {
    if (connection.value) {
      try {
        await connection.value.stop();
      } catch (err) {
        console.error("Error stopping SignalR connection:", err);
      } finally {
        connection.value = null;
        connectionState.value = HubConnectionState.Disconnected;
      }
    }
  };

  const invokeRemoteSignalMethod = async (
    methodName: string,
    ...args: any[]
  ) => {
    if (connection.value && isConnected.value) {
      try {
        await connection.value.invoke(methodName, ...args);
      } catch (err) {
        console.error("Error sending SignalR message:", err);
      }
    }
  };

  const getChatIdsByStatus = (status: RequestStatus) => {
    if (userStore.isUserSupport) {
      return supportAdminStore.requestsIds[status];
    } else {
      return supportUserStore.requestsIds[status];
    }
  };

  const getPageSizeByStatus = (status: RequestStatus) => {
    if (userStore.isUserSupport) {
      return supportAdminStore.pagination[status]!.pageSize;
    } else {
      return supportUserStore.pagination[status]!.pageSize;
    }
  };

  const getPageByStatus = (status: RequestStatus) => {
    if (userStore.isUserSupport) {
      return supportAdminStore.pagination[status]!.current;
    } else {
      return supportUserStore.pagination[status]!.current;
    }
  };

  const setPageByStatus = (status: RequestStatus, page: number) => {
    if (userStore.isUserSupport) {
      supportAdminStore.pagination[status]!.current = page;
    } else {
      supportUserStore.pagination[status]!.current = page;
    }
  };

  const connectToAllUsersChats = async () => {
    if (isConnected.value) {
      try {
        let ids: number[];
        if (userStore.isUserSupport) {
          await supportAdminStore.fetchAllUserChatsIds();
          ids = Array.from(
            new Set(
              Object.values(supportAdminStore.requestsIds).flat(),
            ).values(),
          );
        } else {
          await supportUserStore.fetchAllUserChatsIds();
          ids = Array.from(
            new Set(
              Object.values(supportUserStore.requestsIds).flat(),
            ).values(),
          );
        }
        if (ids.length > 0) {
          await connectToChats(ids);
          connectedRequestIds.value = ids;
        }
      } catch (err) {
        console.error("Error connecting to all users chats:", err);
      }
    }
  };

  const getExistenRequestsIds = async (filterData: ChatIdsFilterDto) => {
    try {
      const idsReq = await CHAT_API.getExistenRequestsIds(filterData);
      return idsReq?.data || [];
    } catch (err) {
      console.error("Error connecting to all users chats:", err);
      return [];
    }
  };

  // REST API methods
  const fetchUserChats = async (filterData: UserChatFilterDto) => {
    try {
      isLoadingChats.value = true;
      const response = await CHAT_API.getUserChats(filterData);

      if (response.status === 200 && response.data) {
        isLoadingChats.value = false;
        return response.data;
      }

      isLoadingChats.value = false;
      return null;
    } catch (err) {
      onError(err);
      isLoadingChats.value = false;
      return null;
    }
  };

  const fetchAdminChats = async (filterData: AdminChatFilterDto) => {
    try {
      isLoadingChats.value = true;
      const response = await CHAT_API.getAdminChats(filterData);

      if (response.status === 200 && response.data) {
        isLoadingChats.value = false;
        return response.data;
      }

      isLoadingChats.value = false;
      return null;
    } catch (err) {
      onError(err);
      isLoadingChats.value = false;
      return null;
    }
  };

  const fetchFirstPageMessages = async (chatId: number) => {
    try {
      isLoadingMessages.value = true;
      const response = await CHAT_API.getMessages(chatId, 0, pageSize.value);

      if (response.status === 200 && response.data) {
        messages.value[chatId] = response.data.messages || [];
        totalMessagesCount.value[chatId] = response.data.count;
      }
    } catch (err) {
      onError(err);
    } finally {
      isLoadingMessages.value = false;
    }
  };

  const loadPreviousMessages = async (chatId: number) => {
    try {
      if (!currentPageNumbers.value[chatId]) {
        currentPageNumbers.value[chatId] = 0;
      }
      currentPageNumbers.value[chatId] = currentPageNumbers.value[chatId]! + 1;
      const currentPageNumber = currentPageNumbers.value[chatId]!;
      const skip = currentPageNumber * pageSize.value; // TODO: take into account new messages added from WS
      const response = await CHAT_API.getMessages(chatId, skip, pageSize.value);

      if (response.status === 200 && response.data) {
        if (!messages.value[chatId]) {
          messages.value[chatId] = [];
        }
        messages.value[chatId].unshift(...(response.data.messages || []));
        totalMessagesCount.value[chatId] = response.data.count;
      }
    } catch (err) {
      onError(err);
    }
  };

  const sendFirstMessage = async (messageData: AddMessageDto) => {
    try {
      isSendingMessage.value = true;
      const response = await CHAT_API.addMessage(messageData);

      if (response.status === 200) {
        const chatId = response.data;

        // If it's a new chat, fetch the updated chat list
        if (!messageData.chatId) {
          await fetchChats();
        }

        createNotification(
          "success",
          "Сообщение отправлено",
          undefined,
          "topRight",
        );

        return chatId;
      }
    } catch (err) {
      onError(err);
      return null;
    } finally {
      isSendingMessage.value = false;
    }
  };

  const assignAdminToChat = async (chatId: number) => {
    try {
      const response = await CHAT_API.addAdminToChat(chatId);

      if (response.status === 200) {
        createNotification(
          "success",
          "Администратор назначен",
          undefined,
          "topRight",
        );

        // Refresh the chat
        await fetchChats();
        return response.data;
      }
    } catch (err) {
      onError(err);
      return null;
    }
  };

  const closeChat = async (chatId: number) => {
    try {
      const response = await CHAT_API.closeChat(chatId);

      if (response.status === 200) {
        createNotification("success", "Чат закрыт", undefined, "topRight");
        await fetchChats();
      }
    } catch (err) {
      onError(err);
    }
  };

  const openChat = async (chatId: number) => {
    try {
      // const response = await CHAT_API.openChat(chatId);

      createNotification(
        "success",
        "Напишите сообщение в чат для открытия обращения заново",
        undefined,
        "topRight",
      );
      await fetchChats();
    } catch (err) {
      onError(err);
    }
  };

  const changeAdminInChat = async (chatId: number, newAdmin: string) => {
    try {
      const response = await CHAT_API.changeAdminInChat(chatId, newAdmin);

      if (response.status === 200) {
        createNotification(
          "success",
          "Администратор изменен",
          undefined,
          "topRight",
        );
      }
    } catch (err) {
      onError(err);
    }
  };

  const setCurrentChat = async (chatId: number | null) => {
    if (!chatId) {
      return;
    }
    await Promise.all([fetchChats(), fetchFirstPageMessages(chatId)]);
    const req = await getCurrentRequest(chatId);
    unreadMessagesCount.value[chatId] = 0;
    const chatStatus = getRequestStatus(req!);
    if (chatStatus) {
      unreadGroupsMessagesCount.value[chatStatus] = 0;
      const statusChatIds = getChatIdsByStatus(chatStatus)!;
      const pageSize = getPageSizeByStatus(chatStatus)!;
      const chatIdIndex = statusChatIds.indexOf(chatId);
      if (chatIdIndex !== -1) {
        const pageWhereChatIs = Math.floor(chatIdIndex / pageSize) + 1;
        const currentPage = getPageByStatus(chatStatus);
        if (currentPage !== pageWhereChatIs) {
          setPageByStatus(chatStatus, pageWhereChatIs);
          await fetchChats();
        }
      }
    }
  };

  const clearAllData = async () => {
    messages.value = {};
    totalMessagesCount.value = {};
    unreadMessagesCount.value = {};
    unreadGroupsMessagesCount.value = {};
    currentRequest.value = null;
    await disconnectWebSocket();
  };

  const fetchChats = async () => {
    if (userStore.isUserSupport) {
      await supportAdminStore.fetchAllAdminChats();
      await supportAdminStore.fetchAllUserChatsIds();
    } else {
      await supportUserStore.fetchAllUserChats();
      await supportUserStore.fetchAllUserChatsIds();
    }
  };

  const getAllSupportAdmins = async () => {
    if (!userStore.isUserSupport) {
      supportAdmins.value = [];
      return [];
    }
    try {
      isLoadingAdmins.value = true;
      const response = await CHAT_API.getSupportAdmins();
      if (response.status === 200 && response.data) {
        supportAdmins.value = response.data.admins;
        isLoadingAdmins.value = false;
        return response.data.admins;
      }
      supportAdmins.value = [];
      isLoadingAdmins.value = false;
      return [];
    } catch (err) {
      onError(err);
      supportAdmins.value = [];
      isLoadingAdmins.value = false;
      return [];
    }
  };

  const getCurrentRequest = async (chatId: number) => {
    try {
      const response = await CHAT_API.getRequestById(chatId);
      if (response.status === 200 && response.data) {
        currentRequest.value = response.data;
        return response.data;
      }
      currentRequest.value = null;
      return null;
    } catch (e) {
      console.error(e);
      currentRequest.value = null;
      return null;
    }
  };

  return {
    messages,
    totalMessagesCount,
    pageSize,

    isLoadingChats,
    isLoadingMessages,
    isSendingMessage,
    isConnected,

    currentMessages,
    currentChatId,
    currentRequest,
    currentSelectedStatus,
    hasCurrentChatPreviousPage,
    unreadMessagesCount,
    connectedRequestIds,
    unreadGroupsMessagesCount,

    supportAdmins,
    isLoadingAdmins,

    // WebSocket methods
    connectWebSocket,
    disconnectWebSocket,
    invokeRemoteSignalMethod,
    connectToChat,
    connectToChats,
    addMessageToChatWS,
    solveChatWS,
    closeChatWS,
    connectToAllUsersChats,

    // REST API methods
    fetchUserChats,
    fetchAdminChats,
    fetchFirstPageMessages,
    sendFirstMessage,
    assignAdminToChat,
    closeChat,
    changeAdminInChat,
    setCurrentChat,
    clearAllData,
    openChat,
    fetchChats,
    getCurrentRequest,
    getAllSupportAdmins,
    loadPreviousMessages,
    getExistenRequestsIds,
  };
});
